import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

type ModalEntry = {
  id: string;
  onClose: () => void;
};

type ModalHistoryApi = {
  push: (id: string, onClose: () => void) => void;
  popFromUi: (id: string) => void;
  hasOpen: () => boolean;
  isConsumingPopstate: () => boolean;
};

const ModalHistoryContext = createContext<ModalHistoryApi | null>(null);

/**
 * Historia przeglądarki dla overlay-modali (parity z MPA HerdModalHistory).
 * Wstecz zamyka najwyższy modal zamiast opuszczać podstronę.
 */
export function ModalHistoryProvider({ children }: { children: ReactNode }) {
  const stackRef = useRef<ModalEntry[]>([]);
  /** Ile kolejnych eventów popstate zignorować (po history.back() z UI). */
  const suppressCountRef = useRef(0);

  useEffect(() => {
    const current =
      history.state && typeof history.state === "object" ? { ...history.state } : {};
    history.replaceState({ ...current, appPage: true }, "", location.href);

    function onPopState() {
      if (suppressCountRef.current > 0) {
        suppressCountRef.current -= 1;
        return;
      }
      const top = stackRef.current.pop();
      if (top) {
        top.onClose();
      }
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const push = useCallback((id: string, onClose: () => void) => {
    if (stackRef.current.some((entry) => entry.id === id)) {
      return;
    }
    stackRef.current.push({ id, onClose });
    const current =
      history.state && typeof history.state === "object" ? { ...history.state } : {};
    history.pushState(
      { ...current, appPage: true, herdModal: true, herdModalId: id },
      "",
      location.href,
    );
  }, []);

  const popFromUi = useCallback((id: string) => {
    const idx = stackRef.current.findIndex((entry) => entry.id === id);
    if (idx === -1) {
      return;
    }
    stackRef.current.splice(idx, 1);
    // NIE czyść suppress w microtask — popstate z history.back() jest asynchroniczny
    // i wcześniej odpalał onClose kolejnego (dolnego) modalu.
    suppressCountRef.current += 1;
    history.back();
  }, []);

  const api = useMemo<ModalHistoryApi>(
    () => ({
      push,
      popFromUi,
      hasOpen: () => stackRef.current.length > 0,
      isConsumingPopstate: () => suppressCountRef.current > 0,
    }),
    [push, popFromUi],
  );

  return (
    <ModalHistoryContext.Provider value={api}>{children}</ModalHistoryContext.Provider>
  );
}

export function useModalHistory(): ModalHistoryApi | null {
  return useContext(ModalHistoryContext);
}

/** Rejestruje otwarty modal w historii — wstecz wywołuje onCloseFromBack. */
export function useModalHistoryEntry(open: boolean, onCloseFromBack: () => void) {
  const historyApi = useModalHistory();
  const idRef = useRef(`modal-${Math.random().toString(36).slice(2)}`);
  const openRef = useRef(false);
  const onCloseRef = useRef(onCloseFromBack);
  onCloseRef.current = onCloseFromBack;

  useEffect(() => {
    if (!historyApi) {
      return;
    }
    const id = idRef.current;
    if (open && !openRef.current) {
      openRef.current = true;
      historyApi.push(id, () => {
        openRef.current = false;
        onCloseRef.current();
      });
      return;
    }
    if (!open && openRef.current) {
      openRef.current = false;
      historyApi.popFromUi(id);
    }
  }, [open, historyApi]);
}
